import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import WebSocketService from './services/WebSocketService';
import { useAuth0 } from '@auth0/auth0-react';
import { v4 as uuidv4 } from 'uuid';
import { getAuth0AccessToken } from './utils/api';
import { playClickSound, playTabSound } from './utils/sounds'; 


// Block Rivals piece definitions (all 21 pieces)
const PIECES = {
  I1: [[1]],
  I2: [[1, 1]],
  I3: [[1, 1, 1]],
  I4: [[1, 1, 1, 1]],
  I5: [[1, 1, 1, 1, 1]],
  L4: [[1, 0], [1, 0], [1, 1]],
  L5: [[1, 0], [1, 0], [1, 0], [1, 1]],
  T4: [[1, 1, 1], [0, 1, 0]],
  T5: [[1, 1, 1], [0, 1, 0], [0, 1, 0]],
  O: [[1, 1], [1, 1]],
  V3: [[1, 0], [1, 1]],
  V5: [[1, 0, 0], [1, 0, 0], [1, 1, 1]],
  Z4: [[1, 1, 0], [0, 1, 1]],
  Z5: [[0, 0, 1], [1, 1, 1], [1, 0, 0]],
  P: [[1, 1], [1, 1], [1, 0]],
  W: [[1, 0, 0], [1, 1, 0], [0, 1, 1]],
  U: [[1, 0, 1], [1, 1, 1]],
  F: [[0, 1, 1], [1, 1, 0], [0, 1, 0]],
  X: [[0, 1, 0], [1, 1, 1], [0, 1, 0]],
  Y: [[0, 1], [1, 1], [0, 1], [0, 1]],
  N: [[0, 1], [1, 1], [1, 0], [1, 0]]
};

// Map block types to piece IDs (0-20)
const PIECE_ID_MAP = Object.keys(PIECES);

// Sound generation functions
const playSound = (frequency, duration) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';

  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

const playPlaceSound = () => {
  playSound(523.25, 0.1);
  setTimeout(() => playSound(659.25, 0.1), 50);
};

const playRotateSound = () => {
  playSound(440, 0.05);
};

const formatTime = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
};

// Utility functions
const rotatePiece = (piece) => {
  const rows = piece.length;
  const cols = piece[0].length;
  const rotated = Array(cols).fill(0).map(() => Array(rows).fill(0));

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      rotated[c][rows - 1 - r] = piece[r][c];
    }
  }
  return rotated;
};

const flipPiece = (piece) => {
  return piece.map(row => [...row].reverse());
};

const initializeBoard = () => {
  return Array(14).fill(0).map(() => Array(14).fill(null));
};

const initializePieces = () => {
  return Object.keys(PIECES).map(id => ({
    id,
    shape: PIECES[id],
    used: false
  }));
};

function MultiplayerGame({ onReturnToMenu, wsUrl, preferredColor = 'blue', viewMode = 'desktop', playerName = 'Player', user = null, isRanked = false, autoReconnect = false }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [gameState, setGameState] = useState(autoReconnect ? 'connecting' : 'setup'); // setup, connecting, identifying, matchmaking, playing, ended
  const [board, setBoard] = useState(initializeBoard());
  const [myPieces, setMyPieces] = useState(initializePieces());
  const [opponentPieces, setOpponentPieces] = useState(initializePieces());
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [pieceTransform, setPieceTransform] = useState({ rotation: 0, flipped: false });
  const [pendingMove, setPendingMove] = useState(null);
  const [legalPositions, setLegalPositions] = useState([]);
  const [score, setScore] = useState({ me: 0, opponent: 0 });
  const [myPlayerInfo, setMyPlayerInfo] = useState(null);
  const [opponentInfo, setOpponentInfo] = useState(null);
  const [currentTurn, setCurrentTurn] = useState(null);
  const [turnNo, setTurnNo] = useState(1);
  const [clientSeq, setClientSeq] = useState(0);
  const [error, setError] = useState(null);
  const [consecutivePasses, setConsecutivePasses] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [playerId, setPlayerId] = useState('');
  const [matchmakingStatus, setMatchmakingStatus] = useState('');
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [dragHoverPosition, setDragHoverPosition] = useState(null);
  const [placedCells, setPlacedCells] = useState(new Set());
  const [turnTimer, setTurnTimer] = useState(60); // 60 seconds per turn
  const [gameCode, setGameCode] = useState(''); // Optional game code for friend matches
  const [gameEndReason, setGameEndReason] = useState(null); // RESIGN, DISCONNECT, or null
  const [winnerPlayerId, setWinnerPlayerId] = useState(null);
  const [showReconnectBanner, setShowReconnectBanner] = useState(false); // Show banner when reconnected
  const [connectionHealth, setConnectionHealth] = useState('healthy'); // 'healthy', 'unstable', 'lost'
  const timerRef = useRef(null);

  // Mobile-specific state
  const [mobileTab, setMobileTab] = useState('your');
  const [touchedPiece, setTouchedPiece] = useState(null);
  const [touchPosition, setTouchPosition] = useState(null);

  // Store player color locally (not sent to backend)
  const [playerColor, setPlayerColor] = useState(preferredColor);

  // Use ref to track if match request has been sent (survives re-renders)
  const matchRequestedRef = useRef(false);

  // Track if this is an auto-reconnect attempt (vs manual matchmaking)
  const isAutoReconnectRef = useRef(false);

  // Refs to store current turnNo and clientSeq for timer callback
  const turnNoRef = useRef(1);
  const clientSeqRef = useRef(0);
  const currentTurnRef = useRef(null);
  const autoPassingRef = useRef(false); // Track if we're auto-passing due to timeout

  // Track processed moves to prevent duplicates
  const processedMovesRef = useRef(new Set());

  // Track current session to prevent re-initialization
  const currentSessionRef = useRef(null);

  // Track if a move is being sent to prevent double-confirm
  const [movePending, setMovePending] = useState(false);

  // Use ref to always have current player info in callbacks
  const myPlayerInfoRef = useRef(null);

  // Use ref to always have current game code in callbacks
  const gameCodeRef = useRef('');

  // Track connection state to prevent showing errors after successful connection
  const hasConnectedRef = useRef(false);

  // Store auth token in ref for use in Identify
  const authTokenRef = useRef(null);

  // Generate or retrieve persisted player ID on mount (UUID for guests)
  useEffect(() => {
    // For guest players, try to retrieve existing UUID from localStorage
    // This allows guests to reconnect to their game after page refresh
    let guestId = localStorage.getItem('blockrivalsGuestPlayerId');
    if (!guestId) {
      guestId = uuidv4();
      localStorage.setItem('blockrivalsGuestPlayerId', guestId);
      console.log('Generated new guest player ID:', guestId);
    } else {
      console.log('Retrieved existing guest player ID from localStorage:', guestId);
    }
    setPlayerId(guestId);
  }, []);

  // Reset match request flag when transitioning to connecting state
  useEffect(() => {
    if (gameState === 'connecting') {
      matchRequestedRef.current = false;
    }
  }, [gameState]);

  // Keep gameCodeRef in sync with gameCode state
  useEffect(() => {
    gameCodeRef.current = gameCode;
  }, [gameCode]);

  // Keep turnNoRef in sync with turnNo state
  useEffect(() => {
    turnNoRef.current = turnNo;
  }, [turnNo]);

  // Keep clientSeqRef in sync with clientSeq state
  useEffect(() => {
    clientSeqRef.current = clientSeq;
  }, [clientSeq]);

  // Keep currentTurnRef in sync with currentTurn state
  useEffect(() => {
    currentTurnRef.current = currentTurn;
  }, [currentTurn]);

  // Connect to WebSocket (only when transitioning from setup to connecting)
  useEffect(() => {
    // Only connect when we first leave setup state (gameState becomes 'connecting')
    if (gameState !== 'connecting') {
      return; // Don't connect yet or already connected
    }

    if (!wsUrl) {
      setError('WebSocket URL not provided');
      return;
    }

    let connectionTimeout = null;
    hasConnectedRef.current = false;

    const connectAndPrepare = async () => {
      // Get API access token if user is authenticated
      if (isAuthenticated) {
        try {
          const accessToken = await getAuth0AccessToken(getAccessTokenSilently);
          authTokenRef.current = accessToken;
          console.log('Got API access token for Identify message');
        } catch (error) {
          console.error('Failed to get access token:', error);
          authTokenRef.current = null;
          // Continue without token - will be treated as guest
        }
      } else {
        authTokenRef.current = null;
      }

      WebSocketService.connect(wsUrl)
        .then(() => {
          hasConnectedRef.current = true;
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
            connectionTimeout = null;
          }
          setConnectionStatus('Connected');
          setGameState('identifying');
          setError(null);
        })
        .catch(err => {
          console.log('WebSocket connection error:', err);
          // Only show error if we haven't connected yet
          // Set timeout to delay error display
          if (!hasConnectedRef.current) {
            connectionTimeout = setTimeout(() => {
              if (!hasConnectedRef.current) {
                setError(`Connection failed: ${err.message}`);
                setConnectionStatus('Connection failed');
              }
            }, 2000);
          }
        });
    };

    connectAndPrepare();

    return () => {
      // Clean up timeout when effect re-runs or component unmounts
      clearTimeout(connectionTimeout);
    };
  }, [wsUrl, gameState, isAuthenticated, getAccessTokenSilently]);

  // Auto-identify when connected
  useEffect(() => {
    if (gameState === 'identifying') {
      const authToken = authTokenRef.current;

      if (authToken && user?.sub) {
        // Authenticated user - use Auth0 sub as playerId, send empty playerName (server will use saved nickname)
        console.log('Sending Identify for authenticated user with sub:', user.sub);
        WebSocketService.identify(user.sub, '', authToken);
      } else if (playerId) {
        // Guest user - use UUID playerId and display name
        console.log('Sending Identify for guest user with playerId:', playerId);
        WebSocketService.identify(playerId, playerName, null);
      }
    }
  }, [gameState, playerId, playerName, user]);

  const handleIdentified = useCallback((message) => {
    console.log('Identified:', message);
    const { body } = message;

    // Check if this is a resume (reconnect to existing game)
    if (body.resume === true && body.sessionId) {
      console.log('Resuming game session:', body.sessionId);
      console.log('🔍 Full Identified body:', JSON.stringify(body, null, 2));
      console.log('🔍 Body keys:', Object.keys(body));

      // Check if the game has already ended
      if (body.gameEnded === true) {
        console.log('⚠️ Attempted to resume an ended game - redirecting to menu');
        setError('This game has already ended');
        setGameState('menu');
        return;
      }

      setConnectionStatus('Reconnecting to game...');
      setGameState('playing');

      // Set session ID
      WebSocketService.sessionId = body.sessionId;
      currentSessionRef.current = body.sessionId;

      // Restore player info
      const iAmFirstPlayer = body.firstPlayer === body.you.playerId;
      const myPlayerInfoWithCorner = {
        ...body.you,
        startingCorner: iAmFirstPlayer ? [4, 4] : [9, 9]
      };
      setMyPlayerInfo(myPlayerInfoWithCorner);
      myPlayerInfoRef.current = myPlayerInfoWithCorner;
      setOpponentInfo(body.opponent);

      // Reconstruct game state from move history
      if (body.moves && body.moves.length > 0) {
        console.log(`Replaying ${body.moves.length} moves to reconstruct game state`);
        setMatchmakingStatus(`Reconnecting... replaying ${body.moves.length} moves`);

        // Reset board and pieces
        processedMovesRef.current.clear();
        const freshBoard = initializeBoard();
        const freshMyPieces = initializePieces();
        const freshOpponentPieces = initializePieces();
        let passCount = 0;
        let latestTurnNo = 1;
        let latestTurnPlayerId = body.firstPlayer;

        console.log(`Replaying ${body.moves.length} moves to reconstruct game state`);

        // Filter out completely empty moves (skip objects with no properties)
        const validMoves = body.moves.filter(move => move && Object.keys(move).length > 0);
        if (validMoves.length !== body.moves.length) {
          console.warn(`⚠️ Found ${body.moves.length - validMoves.length} empty move objects - these will be skipped`);
        }

        // Count how many VALID moves were made by me (to set clientSeq correctly)
        // Empty moves are just serialization artifacts and should NOT be counted
        let myMoveCount = 0;

        console.log(`📜 Full moves array (${body.moves.length} total):`, JSON.stringify(body.moves, null, 2));

        // Count only VALID moves made by me
        validMoves.forEach((move) => {
          if (move.actorPlayerId === body.you.playerId) {
            myMoveCount++;
            console.log(`    📊 Valid move ${move.moveNo} was mine (action: ${move.action}), myMoveCount: ${myMoveCount}`);
          }
        });
        console.log(`📊 Total VALID moves by me in history: ${myMoveCount}`);

        // Replay each valid move for board reconstruction
        validMoves.forEach((move, index) => {
          console.log(`📝 Replaying move ${index + 1}:`, move);

          const moveKey = `${move.moveNo}-${move.actorPlayerId}`;
          processedMovesRef.current.add(moveKey);

          if (move.action === 'PLACE' && move.payload) {
            const { actorPlayerId, payload } = move;
            const { blockType, rotation, flip, x, y } = payload;

            console.log(`  → PLACE: blockType=${blockType}, pos=(${x},${y}), rotation=${rotation}, flip=${flip}`);

            // Get piece shape
            const pieceId = PIECE_ID_MAP[blockType];
            let shape = PIECES[pieceId];

            // Apply transformations
            for (let i = 0; i < rotation; i++) {
              shape = rotatePiece(shape);
            }
            if (flip === 1) {
              shape = flipPiece(shape);
            }

            // Place on board
            shape.forEach((row, r) => {
              row.forEach((cell, c) => {
                if (cell) {
                  const boardRow = y + r;
                  const boardCol = x + c;
                  if (boardRow >= 0 && boardRow < 14 && boardCol >= 0 && boardCol < 14) {
                    freshBoard[boardRow][boardCol] = actorPlayerId;
                    console.log(`    Cell placed at [${boardRow}][${boardCol}]`);
                  }
                }
              });
            });

            // Mark piece as used
            if (actorPlayerId === body.you.playerId) {
              const pieceIndex = freshMyPieces.findIndex(p => p.id === pieceId);
              if (pieceIndex !== -1) {
                freshMyPieces[pieceIndex].used = true;
                console.log(`    Marked your piece ${pieceId} as used`);
              }
            } else {
              const pieceIndex = freshOpponentPieces.findIndex(p => p.id === pieceId);
              if (pieceIndex !== -1) {
                freshOpponentPieces[pieceIndex].used = true;
                console.log(`    Marked opponent piece ${pieceId} as used`);
              }
            }

            passCount = 0; // Reset pass count on PLACE
          } else if (move.action === 'PASS') {
            passCount++;
            console.log(`  → PASS`);
          }

          // Use move.moveNo if available, otherwise use index + 1
          latestTurnNo = move.moveNo || (index + 1);
        });

        // Determine current turn player (alternates based on firstPlayer)
        // Use VALID moves only for turn calculation (empty moves seem to be a serialization artifact)
        // Game starts with first player's turn
        // After move 1 (first player plays) → second player's turn
        // After move 2 (second player plays) → first player's turn
        // After move 3 (first player plays) → second player's turn
        // Pattern: After odd valid moves → second player's turn, After even valid moves (including 0) → first player's turn
        const totalMoves = body.moves.length; // Total including empty
        const totalValidMoves = validMoves.length; // Only actual moves
        const isFirstPlayerTurn = (totalValidMoves % 2) === 0;
        const firstPlayerId = body.firstPlayer;
        // Second player is whoever is NOT the first player
        const secondPlayerId = body.you.playerId === body.firstPlayer ? body.opponent.playerId : body.you.playerId;
        latestTurnPlayerId = isFirstPlayerTurn ? firstPlayerId : secondPlayerId;
        // latestTurnNo is already set correctly from the replay loop above (from move.moveNo)

        console.log(`🎲 Turn calculation:`);
        console.log(`   Total moves in array: ${totalMoves} (${totalMoves % 2 === 0 ? 'even' : 'odd'})`);
        console.log(`   Valid moves: ${totalValidMoves} (${totalValidMoves % 2 === 0 ? 'even' : 'odd'}), Empty moves: ${body.moves.length - validMoves.length}`);
        console.log(`   latestTurnNo after replay loop: ${latestTurnNo}`);
        console.log(`   First player: ${firstPlayerId}`);
        console.log(`   Second player: ${secondPlayerId}`);
        console.log(`   You: ${body.you.playerId}`);
        console.log(`   You are: ${body.you.playerId === firstPlayerId ? 'FIRST PLAYER' : 'SECOND PLAYER'}`);
        console.log(`   → Using VALID move count (${totalValidMoves}) for turn calculation`);
        console.log(`   → Current turn: ${latestTurnPlayerId} (${isFirstPlayerTurn ? 'first player' : 'second player'})`);
        console.log(`   → Is it YOUR turn? ${latestTurnPlayerId === body.you.playerId ? 'YES ✓' : 'NO ✗'}`);
        console.log(`   → Next turnNo will be: ${latestTurnNo + 1}`);

        // Log the reconstructed board
        console.log('📋 Board state after reconstruction:');
        let occupiedCells = 0;
        freshBoard.forEach((row, r) => {
          row.forEach((cell, c) => {
            if (cell) {
              occupiedCells++;
              console.log(`  [${r}][${c}] = ${cell}`);
            }
          });
        });
        console.log(`  Total occupied cells: ${occupiedCells}`);

        // Update all state at once
        setBoard(freshBoard);
        setMyPieces(freshMyPieces);
        setOpponentPieces(freshOpponentPieces);
        setConsecutivePasses(passCount);
        setTurnNo(latestTurnNo + 1); // Next turn number

        console.log(`🔧 SETTING currentTurn state to: ${latestTurnPlayerId}`);
        console.log(`   This is: ${latestTurnPlayerId === body.you.playerId ? 'YOU ✓' : 'OPPONENT ✗'}`);
        console.log(`   Your ID: ${body.you.playerId}`);
        console.log(`   Opponent ID: ${body.opponent.playerId}`);

        setCurrentTurn(latestTurnPlayerId);
        currentTurnRef.current = latestTurnPlayerId;
        turnNoRef.current = latestTurnNo + 1;

        // Set clientSeq based on how many moves I've made
        setClientSeq(myMoveCount);
        clientSeqRef.current = myMoveCount;

        console.log(`✅ Game state reconstructed - Turn: ${latestTurnNo + 1}, Current player: ${latestTurnPlayerId}, ClientSeq: ${myMoveCount}`);
        setMatchmakingStatus('Reconnected to game!');
        setShowReconnectBanner(true);
        setTimeout(() => {
          setMatchmakingStatus('');
          setShowReconnectBanner(false);
        }, 3000);
      } else {
        // No moves yet, just set initial turn
        setBoard(initializeBoard());
        setMyPieces(initializePieces());
        setOpponentPieces(initializePieces());
        setTurnNo(1);
        setCurrentTurn(body.firstPlayer);
        currentTurnRef.current = body.firstPlayer;
        turnNoRef.current = 1;

        // Reset clientSeq since no moves have been made
        setClientSeq(0);
        clientSeqRef.current = 0;

        console.log('Game state reconstructed - Turn: 1, Current player:', body.firstPlayer, 'ClientSeq: 0');
        setMatchmakingStatus('Reconnected to game!');
        setShowReconnectBanner(true);
        setTimeout(() => {
          setMatchmakingStatus('');
          setShowReconnectBanner(false);
        }, 3000);
      }

      return; // Exit early, don't send MatchRequest
    }

    // Normal identify flow (not a resume) - proceed to matchmaking
    setGameState('matchmaking');
    setConnectionStatus('Finding match...');

    // Only send MatchRequest once (guard against duplicate calls)
    if (!matchRequestedRef.current) {
      matchRequestedRef.current = true;
      const userId = user?.sub || null;
      console.log('Sending match request with userId:', userId, 'isRanked:', isRanked);
      WebSocketService.matchRequest(playerName, 'Classic2', null, gameCodeRef.current, userId, isRanked);
    }
  }, [playerName, user, isRanked]);

  const handleMatchQueued = (message) => {
    console.log('Match queued:', message);
    setMatchmakingStatus('Searching for opponent...');
  };

  const handleMatchFound = (message) => {
    console.log('Match found:', message);
    const { body, sessionId } = message;
    console.log('Player colors - You:', body.you.color, 'Opponent:', body.opponent.color);

    // Prevent duplicate initialization of the same session
    if (currentSessionRef.current === sessionId) {
      console.log('Skipping duplicate handleMatchFound for session:', sessionId);
      return;
    }
    currentSessionRef.current = sessionId;

    WebSocketService.sessionId = sessionId;

    // Reset board and pieces for new game
    processedMovesRef.current.clear(); // Clear processed moves for new game
    setBoard(initializeBoard());
    setMyPieces(initializePieces());
    setOpponentPieces(initializePieces());
    setScore({ me: 0, opponent: 0 });
    setSelectedPiece(null);
    setPieceTransform({ rotation: 0, flipped: false });
    setPendingMove(null);
    setLegalPositions([]);

    // Determine if I'm the first player (who goes first) based on initialTurnPlayerId
    const iAmFirstPlayer = body.initialTurnPlayerId === body.you.playerId;

    // Add starting corner info to player info
    const myPlayerInfoWithCorner = {
      ...body.you,
      startingCorner: iAmFirstPlayer ? [4, 4] : [9, 9]
    };

    setMyPlayerInfo(myPlayerInfoWithCorner);
    myPlayerInfoRef.current = myPlayerInfoWithCorner; // Store in ref for immediate access
    setOpponentInfo(body.opponent);
    setCurrentTurn(body.initialTurnPlayerId);
    setTurnNo(body.turnNo);
    setGameState('playing');
    setMatchmakingStatus('Match found! Game starting...');
  };

  const handleTurn = (message) => {
    console.log('🔔 Turn update message received:', message);
    const { body } = message;

    console.log(`   Turn changing to player: ${body.playerId}`);
    console.log(`   TurnNo: ${body.turnNo}`);

    // Update refs immediately for timer callback
    currentTurnRef.current = body.playerId;
    turnNoRef.current = body.turnNo;

    // Update state
    setCurrentTurn(body.playerId);
    setTurnNo(body.turnNo);
  };

  const handleMoveApplied = (message) => {
    console.log('=== MOVE APPLIED ===');
    console.log('Message:', message);
    console.log('Current processed moves:', Array.from(processedMovesRef.current));
    const { body } = message;

    // Prevent duplicate processing of the same move
    const moveKey = `${body.moveNo}-${body.actorPlayerId}`;
    if (processedMovesRef.current.has(moveKey)) {
      console.log('⚠️ Skipping duplicate move:', moveKey);
      return;
    }
    console.log('✓ Processing new move:', moveKey);
    processedMovesRef.current.add(moveKey);

    playPlaceSound();

    // Update board with the move
    if (body.action === 'PLACE' && body.payload) {
      console.log('Applying move to board:', body);
      applyMoveToBoard(body);
      setConsecutivePasses(0); // Reset consecutive passes on a place move
    } else if (body.action === 'PASS') {
      console.log('Player passed');
      setConsecutivePasses(prev => {
        const newCount = prev + 1;
        console.log(`Consecutive passes: ${newCount}`);
        if (newCount >= 2) {
          console.log('Both players passed - waiting for Result message from backend');
          // Don't end the game locally - wait for the backend to send Result message
          // The backend will detect both passes and send the official game result
        }
        return newCount;
      });
    }

    // Update turn
    console.log(`Turn update: ${body.nextTurnPlayerId}, turnNo: ${body.turnNo}`);
    setCurrentTurn(body.nextTurnPlayerId);
    setTurnNo(body.turnNo);

    // Clear pending move if it was ours - use ref to get current value
    if (body.actorPlayerId === myPlayerInfoRef.current?.playerId) {
      console.log('✓ Clearing pending move (was our move)');
      setPendingMove(null);
      setSelectedPiece(null);
      setPieceTransform({ rotation: 0, flipped: false });
      setMovePending(false);
    }
    console.log('===================\n');
  };

  const applyMoveToBoard = (moveData) => {
    const { actorPlayerId, payload } = moveData;
    const { blockType, rotation, flip, x, y } = payload;

    console.log(`📍 applyMoveToBoard called - Player: ${actorPlayerId}, Piece: ${blockType}, Position: [${y}, ${x}]`);

    // Play sound effect for piece placement
    playPlaceSound();

    // Get piece shape
    const pieceId = PIECE_ID_MAP[blockType];
    let shape = PIECES[pieceId];

    // Apply transformations
    for (let i = 0; i < rotation; i++) {
      shape = rotatePiece(shape);
    }
    if (flip === 1) {
      shape = flipPiece(shape);
    }

    // Use the actual player ID (not numeric 1/2) for board cells
    const isMyMove = actorPlayerId === myPlayerInfoRef.current?.playerId;
    console.log(`🎨 Player ${actorPlayerId} (${isMyMove ? 'ME' : 'OPPONENT'}) placing piece (my ID: ${myPlayerInfoRef.current?.playerId})`);

    // Apply to board - use callback to get current board state
    setBoard(prevBoard => {
      console.log('📋 Inside setBoard callback');
      console.log('Previous board state:', prevBoard.map(row => row.filter(cell => cell !== null).length).reduce((a, b) => a + b, 0), 'occupied cells');

      const newBoard = prevBoard.map(r => [...r]);
      const newPlacedCells = new Set();

      let cellsPlaced = 0;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            const boardRow = y + r;
            const boardCol = x + c;
            if (boardRow >= 0 && boardRow < 14 && boardCol >= 0 && boardCol < 14) {
              // Store the actual player ID (UUID) instead of numeric 1/2
              newBoard[boardRow][boardCol] = actorPlayerId;
              newPlacedCells.add(`${boardRow},${boardCol}`);
              cellsPlaced++;
            }
          }
        }
      }

      console.log(`✅ Placed ${cellsPlaced} cells for player ${actorPlayerId}`);
      console.log('New board state:', newBoard.map(row => row.filter(cell => cell !== null).length).reduce((a, b) => a + b, 0), 'occupied cells');

      setPlacedCells(newPlacedCells);

      // Clear animation
      setTimeout(() => {
        setPlacedCells(new Set());
      }, 800);

      return newBoard;
    });

    // Mark piece as used
    if (isMyMove) {
      setMyPieces(prev => prev.map(p =>
        p.id === pieceId ? { ...p, used: true } : p
      ));
      setScore(prev => ({ ...prev, me: prev.me + countTiles(shape) }));
    } else {
      setOpponentPieces(prev => prev.map(p =>
        p.id === pieceId ? { ...p, used: true } : p
      ));
      setScore(prev => ({ ...prev, opponent: prev.opponent + countTiles(shape) }));
    }
  };

  const countTiles = (shape) => {
    let count = 0;
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) count++;
      }
    }
    return count;
  };

  const handleMoveRejected = (message) => {
    console.error('Move rejected:', message);

    // Suppress error if this is from an auto-pass timeout race condition
    if (autoPassingRef.current) {
      console.log('Suppressing move rejection from auto-pass timeout race condition');
      autoPassingRef.current = false;
      return;
    }

    setError(`Move rejected: ${message.body.reason}`);
    // Clear pending move
    setPendingMove(null);
    setMovePending(false);
  };

  const handleResult = (message) => {
    console.log('Game result:', message);
    const { body } = message;
    setGameState('ended');
    setGameEndReason(body.endReason || null);

    // Store winnerPlayerId if provided
    if (body.winnerPlayerId) {
      setWinnerPlayerId(body.winnerPlayerId);
    }

    // Update rating if provided in result (for rated games)
    // Use myPlayerInfoRef.current instead of myPlayerInfo state to ensure we have the value
    const currentPlayerInfo = myPlayerInfoRef.current;

    console.log('🔍 Checking updatedProfiles condition:', {
      hasUpdatedProfiles: !!body.updatedProfiles,
      isArray: Array.isArray(body.updatedProfiles),
      hasMyPlayerInfo: !!currentPlayerInfo,
      myPlayerId: currentPlayerInfo?.playerId,
      updatedProfiles: body.updatedProfiles
    });

    if (body.updatedProfiles && Array.isArray(body.updatedProfiles) && currentPlayerInfo?.playerId) {
      console.log('📊 Rating update received from game result:', body.updatedProfiles);

      // Find my profile in the updatedProfiles array
      const myProfile = body.updatedProfiles.find(p => p.playerId === currentPlayerInfo.playerId);

      if (myProfile && myProfile.rating !== undefined) {
        const newStats = {
          rating: myProfile.rating,
          wins: myProfile.wins,
          losses: myProfile.losses
        };

        // Update localStorage cache immediately so ProfilePage always has latest data
        localStorage.setItem('blockRivalsStats', JSON.stringify(newStats));
        console.log('✅ Updated cached stats in localStorage:', newStats);

        // Dispatch event for ProfilePage if it's currently mounted
        window.dispatchEvent(new CustomEvent('ratingUpdated', { detail: newStats }));
        console.log('📡 Dispatched ratingUpdated event');
      } else {
        console.log('⚠️ My profile not found in updatedProfiles or missing rating data');
      }
    } else {
      console.log('ℹ️ No updatedProfiles in result (might be unranked game or guest player)');
    }

    // Only update scores for normal game end, not for DISCONNECT/RESIGN
    // Frontend already has accurate scores from real-time tracking
    if (body.endReason !== 'DISCONNECT' && body.endReason !== 'RESIGN') {
      // Update final scores - handle both array and object format
      let myScore, oppScore;
      if (Array.isArray(body.scores)) {
        // Old format: array of { playerId, score }
        const myScoreObj = body.scores.find(s => s.playerId === myPlayerInfo?.playerId);
        const oppScoreObj = body.scores.find(s => s.playerId === opponentInfo?.playerId);
        myScore = myScoreObj ? myScoreObj.score : 0;
        oppScore = oppScoreObj ? oppScoreObj.score : 0;
      } else if (typeof body.scores === 'object') {
        // New format: object with playerId as keys
        myScore = body.scores[myPlayerInfo?.playerId] || 0;
        oppScore = body.scores[opponentInfo?.playerId] || 0;
      }

      if (myScore !== undefined && oppScore !== undefined) {
        setScore({ me: myScore, opponent: oppScore });
      }
    }
    // For DISCONNECT/RESIGN: keep the current scores from frontend tracking
  };

  const handleError = (message) => {
    console.error('Server error:', message);

    // Suppress error if this is from an auto-pass timeout race condition
    if (autoPassingRef.current) {
      console.log('Suppressing server error from auto-pass timeout race condition');
      autoPassingRef.current = false;
      return;
    }

    setError(`Server error: ${message.body.message}`);
  };

  // Set up WebSocket message listeners (separate from connection)
  // Run once on mount, clean up on unmount
  useEffect(() => {
    // Add catch-all listener for debugging
    const handleAllMessages = (message) => {
      console.log('🔔 ALL MESSAGES:', message.type, message);
    };
    WebSocketService.on('*', handleAllMessages);

    WebSocketService.on('Identified', handleIdentified);
    WebSocketService.on('MatchQueued', handleMatchQueued);
    WebSocketService.on('MatchFound', handleMatchFound);
    WebSocketService.on('Turn', handleTurn);
    WebSocketService.on('MoveApplied', handleMoveApplied);
    WebSocketService.on('MoveRejected', handleMoveRejected);
    WebSocketService.on('Result', handleResult);
    WebSocketService.on('Error', handleError);

    return () => {
      // Only clean up listeners and disconnect when component unmounts
      WebSocketService.off('*', handleAllMessages);
      WebSocketService.off('Identified', handleIdentified);
      WebSocketService.off('MatchQueued', handleMatchQueued);
      WebSocketService.off('MatchFound', handleMatchFound);
      WebSocketService.off('Turn', handleTurn);
      WebSocketService.off('MoveApplied', handleMoveApplied);
      WebSocketService.off('MoveRejected', handleMoveRejected);
      WebSocketService.off('Result', handleResult);
      WebSocketService.off('Error', handleError);

      WebSocketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start heartbeat when game is playing
  useEffect(() => {
    if (gameState === 'playing') {
      console.log('🫀 Starting heartbeat');
      WebSocketService.startHeartbeat(turnNo);

      // Subscribe to connection health updates
      const unsubscribe = WebSocketService.onConnectionHealth((healthStatus, timeSinceLastPong) => {
        console.log('💓 Connection health:', healthStatus, 'Time since pong:', timeSinceLastPong);
        setConnectionHealth(healthStatus);
      });

      return () => {
        console.log('🫀 Stopping heartbeat');
        WebSocketService.stopHeartbeat();
        unsubscribe();
      };
    }
  }, [gameState, turnNo]);

  // Get current piece shape with transformations
  const getCurrentPieceShape = useCallback(() => {
    if (!selectedPiece) return null;
    let shape = selectedPiece.shape;

    for (let i = 0; i < pieceTransform.rotation; i++) {
      shape = rotatePiece(shape);
    }

    if (pieceTransform.flipped) {
      shape = flipPiece(shape);
    }

    return shape;
  }, [selectedPiece, pieceTransform]);

  // Check if position is valid on board
  const isValidPosition = (row, col, shape) => {
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) {
          const boardRow = row + r;
          const boardCol = col + c;
          if (boardRow < 0 || boardRow >= 14 || boardCol < 0 || boardCol >= 14) {
            return false;
          }
          if (board[boardRow][boardCol] !== null) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Check if move is legal (follows Blokus rules)
  const isLegalMove = useCallback((row, col, shape) => {
    if (!isValidPosition(row, col, shape)) return { legal: false };

    const isFirstMove = myPieces.filter(p => p.used).length === 0;

    // First move must touch the player's starting corner
    if (isFirstMove) {
      const corner = myPlayerInfo?.startingCorner || [4, 4]; // Fallback to [4,4] if not set
      let touchesCorner = false;

      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            const boardRow = row + r;
            const boardCol = col + c;
            if (boardRow === corner[0] && boardCol === corner[1]) {
              touchesCorner = true;
            }
          }
        }
      }

      if (!touchesCorner) return { legal: false };
      return { legal: true, connectingCorner: corner };
    }

    // Check for edge adjacency (not allowed)
    let hasEdgeAdjacency = false;
    let hasCornerConnection = false;
    let connectingCorner = null;

    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) {
          const boardRow = row + r;
          const boardCol = col + c;

          // Check edges
          const edges = [
            [boardRow - 1, boardCol],
            [boardRow + 1, boardCol],
            [boardRow, boardCol - 1],
            [boardRow, boardCol + 1]
          ];

          for (const [er, ec] of edges) {
            if (er >= 0 && er < 14 && ec >= 0 && ec < 14) {
              // Check if this edge is occupied by MY pieces
              if (board[er][ec] === myPlayerInfo?.playerId) {
                hasEdgeAdjacency = true;
              }
            }
          }

          // Check corners
          const corners = [
            [boardRow - 1, boardCol - 1],
            [boardRow - 1, boardCol + 1],
            [boardRow + 1, boardCol - 1],
            [boardRow + 1, boardCol + 1]
          ];

          for (const [cr, cc] of corners) {
            if (cr >= 0 && cr < 14 && cc >= 0 && cc < 14) {
              // Check if this corner is occupied by MY pieces
              if (board[cr][cc] === myPlayerInfo?.playerId) {
                hasCornerConnection = true;
                connectingCorner = [cr, cc];
              }
            }
          }
        }
      }
    }

    if (hasEdgeAdjacency) return { legal: false };
    if (!hasCornerConnection) return { legal: false };

    return { legal: true, connectingCorner };
  }, [board, myPieces, myPlayerInfo]);

  // Find all legal positions
  const findLegalPositions = useCallback(() => {
    if (!selectedPiece || gameState !== 'playing') return [];

    const shape = getCurrentPieceShape();
    if (!shape) return [];

    const legalTiles = new Map();

    for (let row = 0; row < 14; row++) {
      for (let col = 0; col < 14; col++) {
        const result = isLegalMove(row, col, shape);

        if (result.legal) {
          for (let r = 0; r < shape.length; r++) {
            for (let c = 0; c < shape[0].length; c++) {
              if (shape[r][c] === 1) {
                const tileRow = row + r;
                const tileCol = col + c;
                const key = `${tileRow},${tileCol}`;

                if (!legalTiles.has(key)) {
                  legalTiles.set(key, {
                    row: tileRow,
                    col: tileCol,
                    placementRow: row,
                    placementCol: col
                  });
                }
              }
            }
          }
        }
      }
    }

    return Array.from(legalTiles.values());
  }, [selectedPiece, getCurrentPieceShape, isLegalMove, gameState]);

  // Update legal positions when piece or transform changes
  useEffect(() => {
    console.log('🔄 Legal positions effect triggered:', {
      hasSelectedPiece: !!selectedPiece,
      selectedPieceId: selectedPiece?.id,
      isMyTurn: currentTurn === myPlayerInfo?.playerId,
      currentTurn,
      myPlayerId: myPlayerInfo?.playerId,
      gameState
    });

    if (selectedPiece && currentTurn === myPlayerInfo?.playerId) {
      const positions = findLegalPositions();
      console.log('✅ Calculated legal positions:', positions.length, 'positions');
      if (positions.length > 0) {
        console.log('Sample positions:', positions.slice(0, 3));
      }
      setLegalPositions(positions);
    } else {
      console.log('❌ Not calculating legal positions - conditions not met');
      setLegalPositions([]);
    }
  }, [selectedPiece, pieceTransform, findLegalPositions, currentTurn, myPlayerInfo, board, myPieces, opponentPieces]);

  // Reset timer when turn changes (separate from countdown logic)
  useEffect(() => {
    if (gameState === 'playing' && currentTurn) {
      setTurnTimer(60);
    }
  }, [currentTurn, gameState]);

  // Timer countdown management
  useEffect(() => {
    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Only run timer during active gameplay
    if (gameState === 'playing' && currentTurn) {
      // Start countdown
      timerRef.current = setInterval(() => {
        setTurnTimer(prev => {
          if (prev <= 1) {
            // Timer expired
            clearInterval(timerRef.current);
            timerRef.current = null;

            // Auto-pass if it's my turn (use ref to get current value)
            if (currentTurnRef.current === myPlayerInfo?.playerId) {
              console.log('Turn timer expired, auto-passing');
              // Use refs to get current values (avoid stale closure)
              const newClientSeq = clientSeqRef.current + 1;
              clientSeqRef.current = newClientSeq; // Update ref immediately
              setClientSeq(newClientSeq);

              // Set flag to suppress errors from race condition with server timeout
              autoPassingRef.current = true;
              WebSocketService.pass(turnNoRef.current, newClientSeq);

              // Clear UI state
              setSelectedPiece(null);
              setPendingMove(null);
              setLegalPositions([]);
            } else {
              console.log('Turn timer expired but not my turn anymore, skipping auto-pass');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentTurn, gameState, myPlayerInfo]);

  const handlePieceSelect = (piece) => {
    if (currentTurn !== myPlayerInfo?.playerId || piece.used || gameState !== 'playing') return;

    setSelectedPiece(piece);
    setPieceTransform({ rotation: 0, flipped: false });
    setPendingMove(null);
  };

  const handleRotate = () => {
    if (!selectedPiece) return;
    playRotateSound();

    const newTransform = {
      rotation: (pieceTransform.rotation + 1) % 4,
      flipped: pieceTransform.flipped
    };
    setPieceTransform(newTransform);

    if (pendingMove) {
      setPendingMove(null);
    }
  };

  const handleFlip = () => {
    if (!selectedPiece) return;
    playRotateSound();

    const newTransform = {
      rotation: pieceTransform.rotation,
      flipped: !pieceTransform.flipped
    };
    setPieceTransform(newTransform);

    if (pendingMove) {
      setPendingMove(null);
    }
  };

  const handleBoardClick = (e, row, col) => {
    if (e) {
      e.stopPropagation();
      // Don't preventDefault - it might interfere with form behavior
    }

    console.log('🎯 Board clicked:', { row, col });
    console.log('📊 State check:', {
      hasSelectedPiece: !!selectedPiece,
      selectedPieceId: selectedPiece?.id,
      currentTurn,
      myPlayerId: myPlayerInfo?.playerId,
      isMyTurn: currentTurn === myPlayerInfo?.playerId,
      hasPendingMove: !!pendingMove,
      gameState
    });

    if (!selectedPiece) {
      console.log('❌ No piece selected');
      return;
    }
    if (currentTurn !== myPlayerInfo?.playerId) {
      console.log('❌ Not your turn');
      return;
    }
    if (pendingMove) {
      console.log('❌ Already have pending move');
      return;
    }
    if (gameState !== 'playing') {
      console.log('❌ Game state is not playing:', gameState);
      return;
    }

    const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    if (!legalPos) {
      console.log('❌ Not a legal position');
      return;
    }
    console.log('✅ Legal position found, creating pending move');

    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape);

    if (result.legal) {
      setPendingMove({
        row: legalPos.placementRow,
        col: legalPos.placementCol,
        shape,
        pieceId: selectedPiece.id,
        anchorCell: result.connectingCorner
      });
    }
  };

  const handleConfirm = (e) => {
    // Prevent event propagation and default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('🎯 handleConfirm called', { hasPendingMove: !!pendingMove, movePending });
    console.trace('📍 handleConfirm call stack');
    if (!pendingMove || movePending) {
      console.log('❌ handleConfirm early return:', { pendingMove: !!pendingMove, movePending });
      return;
    }

    const { row, col, pieceId } = pendingMove;

    // Get block type index
    const blockType = PIECE_ID_MAP.indexOf(pieceId);

    // Set move as pending to prevent double-confirm
    setMovePending(true);

    // Send place message
    const newClientSeq = clientSeq + 1;
    clientSeqRef.current = newClientSeq; // Update ref immediately
    setClientSeq(newClientSeq);

    console.log('📤 Sending Place message:', {
      blockType,
      rotation: pieceTransform.rotation,
      flip: pieceTransform.flipped ? 1 : 0,
      x: col,
      y: row,
      turnNo,
      clientSeq: newClientSeq,
      myPlayerId: myPlayerInfo?.playerId,
      sessionId: WebSocketService.sessionId
    });

    WebSocketService.place({
      blockType,
      rotation: pieceTransform.rotation,
      flip: pieceTransform.flipped ? 1 : 0,
      x: col,
      y: row
    }, turnNo, newClientSeq);
  };

  const handleUndo = () => {
    setPendingMove(null);
  };

  const handlePass = () => {
    const newClientSeq = clientSeq + 1;
    clientSeqRef.current = newClientSeq; // Update ref immediately
    setClientSeq(newClientSeq);

    console.log('📤 Sending Pass:', {
      turnNo,
      clientSeq: newClientSeq,
      currentClientSeq: clientSeq,
      myPlayerId: myPlayerInfo?.playerId
    });

    WebSocketService.pass(turnNo, newClientSeq);

    setSelectedPiece(null);
    setPendingMove(null);
    setLegalPositions([]);
  };

  const handleResign = () => {
    WebSocketService.resign('player-resign');
  };

  // Drag and drop handlers
  const handleDragStart = (e, piece) => {
    if (currentTurn !== myPlayerInfo?.playerId || piece.used || gameState !== 'playing') {
      e.preventDefault();
      return;
    }
    setDraggedPiece(piece);

    if (selectedPiece?.id !== piece.id) {
      handlePieceSelect(piece);
    }

    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, row, col) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedPiece || !selectedPiece || currentTurn !== myPlayerInfo?.playerId || pendingMove || gameState !== 'playing') {
      setDragHoverPosition(null);
      return;
    }

    const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    if (!legalPos) {
      setDragHoverPosition(null);
      return;
    }

    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape);

    if (result.legal) {
      setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleDrop = (e, row, col) => {
    e.preventDefault();
    if (!draggedPiece || !selectedPiece || currentTurn !== myPlayerInfo?.playerId || pendingMove || gameState !== 'playing') return;

    handleBoardClick(e, row, col);
    setDraggedPiece(null);
    setDragHoverPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedPiece(null);
    setDragHoverPosition(null);
  };

  const handleMouseEnter = (row, col) => {
    if (!selectedPiece || currentTurn !== myPlayerInfo?.playerId || pendingMove || draggedPiece || gameState !== 'playing') {
      return;
    }

    const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    if (!legalPos) {
      setDragHoverPosition(null);
      return;
    }

    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape);

    if (result.legal) {
      setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleMouseLeave = () => {
    if (!draggedPiece) {
      setDragHoverPosition(null);
    }
  };

  // Find nearest legal position for touch/drag
  const findNearestLegalPosition = (clientX, clientY, threshold) => {
    // Use responsive threshold based on view mode if not provided
    if (threshold === undefined) {
      threshold = viewMode === 'mobile' ? 30 : 50;
    }

    // Get all board cells
    const boardCells = document.querySelectorAll('.board .cell');
    let nearestPosition = null;
    let minDistance = Infinity;

    boardCells.forEach(cell => {
      const row = parseInt(cell.dataset.row);
      const col = parseInt(cell.dataset.col);

      // Check if this is a legal position
      const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
      if (!legalPos) return;

      // Calculate distance from touch position to cell center
      const rect = cell.getBoundingClientRect();
      const cellCenterX = rect.left + rect.width / 2;
      const cellCenterY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(clientX - cellCenterX, 2) +
        Math.pow(clientY - cellCenterY, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestPosition = legalPos;
      }
    });

    // Only return if within threshold
    if (minDistance <= threshold) {
      return nearestPosition;
    }
    return null;
  };

  // Touch handlers for mobile
  const handleTouchStart = (e, piece) => {
    if (currentTurn !== myPlayerInfo?.playerId || piece.used || gameState !== 'playing') {
      return;
    }

    e.preventDefault();
    setTouchedPiece(piece);

    // Select the piece if not already selected
    if (selectedPiece?.id !== piece.id) {
      handlePieceSelect(piece);
    }

    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    // Always prevent default to stop scrolling when touching a piece
    if (touchedPiece) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!touchedPiece || !selectedPiece || currentTurn !== myPlayerInfo?.playerId || pendingMove || gameState !== 'playing') {
      return;
    }

    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });

    // First try to find exact cell at touch position
    let legalPos = null;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row !== undefined && element.dataset.col !== undefined) {
      const row = parseInt(element.dataset.row);
      const col = parseInt(element.dataset.col);
      legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    }

    // If no exact match, find nearest legal position within threshold
    if (!legalPos) {
      legalPos = findNearestLegalPosition(touch.clientX, touch.clientY);
    }

    if (legalPos) {
      const shape = getCurrentPieceShape();
      const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape);

      if (result.legal) {
        setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
      } else {
        setDragHoverPosition(null);
      }
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchedPiece || !selectedPiece || currentTurn !== myPlayerInfo?.playerId || gameState !== 'playing') {
      setTouchedPiece(null);
      setTouchPosition(null);
      setDragHoverPosition(null);
      return;
    }

    e.preventDefault();
    const touch = e.changedTouches[0];

    // First try to find exact cell at touch position
    let targetRow = null;
    let targetCol = null;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row !== undefined && element.dataset.col !== undefined) {
      targetRow = parseInt(element.dataset.row);
      targetCol = parseInt(element.dataset.col);
    } else {
      // If no exact cell found, try to find nearest legal position
      const nearestPos = findNearestLegalPosition(touch.clientX, touch.clientY);
      if (nearestPos) {
        targetRow = nearestPos.row;
        targetCol = nearestPos.col;
      }
    }

    // Place piece if we found a valid position
    if (targetRow !== null && targetCol !== null) {
      handleBoardClick(null, targetRow, targetCol);
    }

    setTouchedPiece(null);
    setTouchPosition(null);
    setDragHoverPosition(null);
  };

  // Render board
  const renderBoard = () => {
    const boardDisplay = board.map(r => [...r]);

    if (pendingMove) {
      const { row, col, shape } = pendingMove;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            boardDisplay[row + r][col + c] = 'preview';
          }
        }
      }
    } else if (dragHoverPosition) {
      const { row, col, shape } = dragHoverPosition;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            boardDisplay[row + r][col + c] = 'preview';
          }
        }
      }
    }

    return boardDisplay;
  };

  const boardDisplay = renderBoard();

  const getContrastingColor = (color) => {
    const contrasts = {
      'blue': 'red',
      'red': 'blue',
      'orange': 'purple',
      'purple': 'orange'
    };
    return contrasts[color] || 'red';
  };

  const getColorStyles = (color) => {
    const colors = {
      blue: { bg: '#3b82f6', border: '#2563eb' },
      red: { bg: '#ef4444', border: '#dc2626' },
      orange: { bg: '#f97316', border: '#ea580c' },
      purple: { bg: '#a855f7', border: '#9333ea' }
    };
    return colors[color] || colors.blue;
  };

  // Show setup screen
  if (gameState === 'setup') {
    return (
      <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
        <h1>Block Rivals</h1>
        <div className="menu-container">
          <div className="game-code-setup">
            <h2>{isRanked ? 'Ranked Match' : 'Multiplayer Setup'}</h2>
            {isRanked ? (
              <p className="ranked-info">You are entering ranked mode. Your match will affect your rating!</p>
            ) : (
              <p>Enter a game code to play with a friend, or leave blank for random matchmaking</p>
            )}
            <div className="setup-buttons">
              <button
                onClick={() => { playTabSound(); setGameState('connecting'); }}
                className="menu-btn primary"
              >
                {isRanked ? 'Start Ranked Match' : (gameCode.trim() ? 'Play Against Friend' : 'Enter Matchmaking')}
              </button>
              <button onClick={() => { playClickSound(); onReturnToMenu(); }} className="menu-btn secondary">
                Back to Menu
              </button>
            </div>
            {!isRanked && <div className="code-input-container">
              <label htmlFor="game-code">Game Code (Optional):</label>
              <input
                id="game-code"
                type="text"
                value={gameCode}
                onChange={(e) => setGameCode(e.target.value)}
                placeholder="Enter code"
                maxLength={20}
                className="game-code-input"
              />
            </div>}
          </div>
        </div>
      </div>
    );
  }

  // Show connecting/matchmaking screen
  if (gameState === 'connecting' || gameState === 'identifying' || gameState === 'matchmaking') {
    return (
      <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
        <h1>Block Rivals</h1>
        <div className="matchmaking-container">
          <div className="matchmaking-status">
            <h2>{connectionStatus}</h2>
            {matchmakingStatus && <p>{matchmakingStatus}</p>}
            {gameCode && <p className="game-code-display">Game Code: <strong>{gameCode}</strong></p>}
            {error && <div className="error-message">{error}</div>}
            <div className="loading-spinner"></div>
          </div>
          <button onClick={onReturnToMenu} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Show game over screen
  if (gameState === 'ended') {
    let iWon, isTie, endMessage = '';

    // For RESIGN/DISCONNECT: use winnerPlayerId to determine outcome
    if ((gameEndReason === 'RESIGN' || gameEndReason === 'DISCONNECT') && winnerPlayerId) {
      iWon = winnerPlayerId === myPlayerInfo?.playerId;

      if (gameEndReason === 'RESIGN') {
        endMessage = iWon ? `${opponentInfo?.playerName} resigned. You Win!` : 'You resigned.';
      } else {
        endMessage = iWon ? `${opponentInfo?.playerName} disconnected. You Win!` : 'You disconnected.';
      }
    } else {
      // Use score comparison for normal game end
      iWon = score.me > score.opponent;
      isTie = score.me === score.opponent;
      endMessage = isTie ? "It's a Tie!" : iWon ? 'You Win!' : 'You Lose!';
    }

    return (
      <div className="App">
        <h1>Block Rivals</h1>
        <div className="game-over">
          <h2>Game Over!</h2>
          <p>{endMessage}</p>
          <div className="final-scores">
            <div>Your Score: {score.me}</div>
            <div>{opponentInfo?.playerName}'s Score: {score.opponent}</div>
          </div>
          <button onClick={onReturnToMenu} className="new-game-btn">
            Return to Menu
          </button>
        </div>
      </div>
    );
  }

  const isMyTurn = currentTurn === myPlayerInfo?.playerId;

  // Debug logging for turn display issue
  console.log(`🎯 Render: isMyTurn=${isMyTurn}, currentTurn=${currentTurn}, myPlayerInfo?.playerId=${myPlayerInfo?.playerId}`);

  // Mobile view layout
  if (viewMode === 'mobile') {
    const boardDisplay = renderBoard();
    return (
      <div className="App mobile-view">
        <div className="title-container">
          <h1>Block Rivals - Multiplayer</h1>
          <button onClick={handleResign} className="menu-btn-title" title="Resign">
            ✕
          </button>
        </div>

        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {showReconnectBanner && (
          <div className="reconnect-banner">
            ✓ Successfully reconnected to your game!
          </div>
        )}

        {connectionHealth === 'unstable' && (
          <div className="connection-warning-banner">
            ⚠️ Connection is unstable...
          </div>
        )}

        {connectionHealth === 'lost' && (
          <div className="connection-lost-banner">
            ✕ Connection lost. Reconnecting...
          </div>
        )}

        <div className="mobile-game-container">
          <div className="mobile-player-info-container">
            <div
              className={`player-info mobile-player-card ${isMyTurn ? 'active' : ''} ${mobileTab === 'your' ? 'selected-tab' : ''}`}
              onClick={() => setMobileTab('your')}
              style={{ cursor: 'pointer' }}
            >
              <div className="mobile-info-row">
                <span className="mobile-player-name">{myPlayerInfo?.playerName || 'You'}</span>
                <span className="mobile-score-text">{score.me}</span>
                {isMyTurn && (
                  <span className="mobile-timer-text">{turnTimer}s</span>
                )}
              </div>
            </div>

            <div
              className={`player-info mobile-player-card ${!isMyTurn ? 'active' : ''} ${mobileTab === 'opponent' ? 'selected-tab' : ''}`}
              onClick={() => setMobileTab('opponent')}
              style={{ cursor: 'pointer' }}
            >
              <div className="mobile-info-row">
                <span className="mobile-player-name">{opponentInfo?.playerName || 'Opponent'}</span>
                <span className="mobile-score-text">{score.opponent}</span>
                {!isMyTurn && (
                  <span className="mobile-timer-text">{turnTimer}s</span>
                )}
              </div>
            </div>
          </div>

          <div className="game-over-placeholder">
            {gameState === 'ended' && (
              <div className="game-over">
                <h2>Game Over!</h2>
                <p>
                  {winnerPlayerId === myPlayerInfo?.playerId
                    ? 'You Win!'
                    : winnerPlayerId === opponentInfo?.playerId
                    ? `${opponentInfo?.playerName} Wins!`
                    : "It's a Tie!"}
                </p>
                {gameEndReason === 'RESIGN' && <p>Game ended by resignation</p>}
                {gameEndReason === 'DISCONNECT' && <p>Opponent disconnected</p>}
                <button onClick={onReturnToMenu} className="new-game-btn">Return to Menu</button>
              </div>
            )}
          </div>

          <div className="board" style={{
            '--player1-color': getColorStyles(playerColor).bg,
            '--player1-border': getColorStyles(playerColor).border,
            '--player2-color': getColorStyles(getContrastingColor(playerColor)).bg,
            '--player2-border': getColorStyles(getContrastingColor(playerColor)).border
          }}>
            {boardDisplay.map((row, r) => (
              <div key={r} className="board-row">
                {row.map((cell, c) => {
                  const isLegal = !pendingMove && legalPositions.some(pos => pos.row === r && pos.col === c);
                  const myCorner = myPlayerInfo?.startingCorner || [4, 4];
                  const oppCorner = myCorner[0] === 4 ? [9, 9] : [4, 4];
                  const isMyStart = r === myCorner[0] && c === myCorner[1];
                  const isOppStart = r === oppCorner[0] && c === oppCorner[1];
                  const isPlaced = placedCells.has(`${r},${c}`);

                  const isMyPiece = cell === myPlayerInfo?.playerId;
                  const isOpponentPiece = cell && cell !== myPlayerInfo?.playerId && cell !== 'preview';
                  const player1Styles = isMyPiece ? getColorStyles(playerColor) : {};
                  const player2Styles = isOpponentPiece ? getColorStyles(getContrastingColor(playerColor)) : {};
                  const cellStyle = isMyPiece ? {
                    backgroundColor: player1Styles.bg,
                    borderColor: player1Styles.border
                  } : isOpponentPiece ? {
                    backgroundColor: player2Styles.bg,
                    borderColor: player2Styles.border
                  } : {};

                  return (
                    <div
                      key={c}
                      className={`cell ${isMyPiece ? 'player1' : isOpponentPiece ? 'player2' : ''}
                                 ${cell === 'preview' ? 'preview' : ''}
                                 ${isLegal ? 'legal' : ''}
                                 ${isMyStart ? 'start-corner-p1' : ''}
                                 ${isOppStart ? 'start-corner-p2' : ''}
                                 ${isPlaced ? 'placed' : ''}`}
                      style={cellStyle}
                      data-row={r}
                      data-col={c}
                      onClick={(e) => handleBoardClick(e, r, c)}
                      onDragOver={(e) => handleDragOver(e, r, c)}
                      onDrop={(e) => handleDrop(e, r, c)}
                      onMouseEnter={() => handleMouseEnter(r, c)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          <div className="controls">
            <button
              type="button"
              onClick={handleRotate}
              disabled={!isMyTurn || !selectedPiece || gameState !== 'playing'}
            >
              Rotate
            </button>
            <button
              type="button"
              onClick={handleFlip}
              disabled={!isMyTurn || !selectedPiece || gameState !== 'playing'}
            >
              Flip
            </button>
            <button
              type="button"
              onClick={handleUndo}
              className="undo-btn"
              disabled={!pendingMove || gameState !== 'playing'}
            >
              Undo
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="confirm-btn"
              disabled={!pendingMove || gameState !== 'playing' || connectionHealth === 'lost'}
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={handlePass}
              className="pass-btn"
              disabled={!isMyTurn || gameState !== 'playing' || connectionHealth === 'lost'}
            >
              Pass
            </button>
          </div>

          {/* Floating piece that follows finger during touch drag */}
          {touchedPiece && touchPosition && selectedPiece && (
            <div
              className="floating-piece"
              style={{
                position: 'fixed',
                left: `${touchPosition.x}px`,
                top: `${touchPosition.y}px`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 9999,
                opacity: 0.8
              }}
            >
              {(() => {
                let displayShape = selectedPiece.shape;
                for (let i = 0; i < pieceTransform.rotation; i++) {
                  displayShape = rotatePiece(displayShape);
                }
                if (pieceTransform.flipped) {
                  displayShape = flipPiece(displayShape);
                }

                // Calculate bounding box
                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;
                displayShape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;

                return (
                  <div className="piece-grid" style={{ width: `${cols * 20}px` }}>
                    {Array.from({ length: rows }, (_, r) =>
                      Array.from({ length: cols }, (_, c) => {
                        const actualR = r + minRow;
                        const actualC = c + minCol;
                        const isFilled = displayShape[actualR]?.[actualC] === 1;
                        return (
                          <div
                            key={`${r}-${c}`}
                            className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                            style={{
                              backgroundColor: isFilled ? getColorStyles(playerColor).bg : 'transparent',
                              borderColor: isFilled ? getColorStyles(playerColor).border : 'rgba(0, 0, 0, 0.9)',
                              visibility: isFilled ? 'visible' : 'hidden',
                              width: '20px',
                              height: '20px'
                            }}
                          />
                        );
                      })
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="mobile-pieces-container">
            {mobileTab === 'your' ? (
              <div className="pieces-grid">
                {[...myPieces].sort((a, b) => {
                  // Sort used pieces to the end
                  if (a.used !== b.used) return a.used ? 1 : -1;
                  // Among unused pieces, sort by size (larger first)
                  const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                  const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                  return sizeB - sizeA;
                }).map(piece => {
                  const isSelected = selectedPiece?.id === piece.id;
                  let displayShape = piece.shape;

                  if (isSelected) {
                    for (let i = 0; i < pieceTransform.rotation; i++) {
                      displayShape = rotatePiece(displayShape);
                    }
                    if (pieceTransform.flipped) {
                      displayShape = flipPiece(displayShape);
                    }
                  }

                  // Calculate bounding box
                  let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                  displayShape.forEach((row, r) => {
                    row.forEach((cell, c) => {
                      if (cell === 1) {
                        minRow = Math.min(minRow, r);
                        maxRow = Math.max(maxRow, r);
                        minCol = Math.min(minCol, c);
                        maxCol = Math.max(maxCol, c);
                      }
                    });
                  });

                  const cols = maxCol - minCol + 1;
                  const rows = maxRow - minRow + 1;

                  const isTouched = touchedPiece?.id === piece.id;
                  const isPending = pendingMove && selectedPiece?.id === piece.id;

                  return (
                    <div
                      key={piece.id}
                      className={`piece ${piece.used ? 'used' : ''} ${isSelected ? 'selected' : ''} ${isPending ? 'pending' : ''}`}
                      onClick={() => handlePieceSelect(piece)}
                      draggable={!piece.used && isMyTurn && gameState === 'playing'}
                      onDragStart={(e) => handleDragStart(e, piece)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, piece)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div className="piece-grid" style={{ width: `${cols * 2.16}vw`, opacity: isTouched ? 0 : 1 }}>
                        {Array.from({ length: rows }, (_, r) =>
                          Array.from({ length: cols }, (_, c) => {
                            const actualR = r + minRow;
                            const actualC = c + minCol;
                            const isFilled = displayShape[actualR]?.[actualC] === 1;
                            return (
                              <div
                                key={`${r}-${c}`}
                                className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                                style={{
                                  backgroundColor: isFilled && !isPending ? getColorStyles(playerColor).bg : 'transparent',
                                  borderColor: isFilled ? getColorStyles(playerColor).border : 'transparent',
                                  visibility: isFilled ? 'visible' : 'hidden'
                                }}
                              />
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pieces-grid opponent-pieces">
                {[...opponentPieces].sort((a, b) => {
                  // Sort used pieces to the end
                  if (a.used !== b.used) return a.used ? 1 : -1;
                  // Among unused pieces, sort by size (larger first)
                  const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                  const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                  return sizeB - sizeA;
                }).map(piece => {
                  // Calculate bounding box
                  let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                  piece.shape.forEach((row, r) => {
                    row.forEach((cell, c) => {
                      if (cell === 1) {
                        minRow = Math.min(minRow, r);
                        maxRow = Math.max(maxRow, r);
                        minCol = Math.min(minCol, c);
                        maxCol = Math.max(maxCol, c);
                      }
                    });
                  });

                  const cols = maxCol - minCol + 1;
                  const rows = maxRow - minRow + 1;

                  return (
                    <div
                      key={piece.id}
                      className={`piece ${piece.used ? 'used' : ''}`}
                    >
                      <div className="piece-grid" style={{ width: `${cols * 2.16}vw` }}>
                        {Array.from({ length: rows }, (_, r) =>
                          Array.from({ length: cols }, (_, c) => {
                            const actualR = r + minRow;
                            const actualC = c + minCol;
                            const isFilled = piece.shape[actualR]?.[actualC] === 1;
                            return (
                              <div
                                key={`${r}-${c}`}
                                className={`piece-cell ${isFilled ? 'filled player2-piece' : ''}`}
                                style={{
                                  backgroundColor: isFilled ? getColorStyles(getContrastingColor(playerColor)).bg : 'transparent',
                                  borderColor: isFilled ? getColorStyles(getContrastingColor(playerColor)).border : 'rgba(0, 0, 0, 0.9)',
                                  visibility: isFilled ? 'visible' : 'hidden'
                                }}
                              />
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop view layout
  return (
    <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
      <div className="title-container">
        <h1>Block Duel - Multiplayer</h1>
        <button onClick={handleResign} className="menu-btn-title" title="Resign">
          ✕
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {showReconnectBanner && (
        <div className="reconnect-banner">
          ✓ Successfully reconnected to your game!
        </div>
      )}

      {connectionHealth === 'unstable' && (
        <div className="connection-warning-banner">
          ⚠️ Connection is unstable...
        </div>
      )}

      {connectionHealth === 'lost' && (
        <div className="connection-lost-banner">
          ✕ Connection lost. Reconnecting...
        </div>
      )}

      <div className="game-container">
        <div className="side-panel">
          <div className={`player-info ${isMyTurn ? 'active' : ''}`}>
            <h3>{myPlayerInfo?.playerName}</h3>
            <div className="score-section">
              <div className="score-label">Score</div>
              <div className="score-value">{score.me}</div>
            </div>
            {isMyTurn ? (
              <>
                <div className="turn-indicator">Your Turn!</div>
                <div className="timer-section">
                  <div className="timer-text">{turnTimer}s</div>
                </div>
              </>
            ) : (
              <div className="turn-indicator">Their Turn</div>
            )}
          </div>

          <div className="pieces-container">
            <h3>Your Pieces</h3>
            <div className="pieces-grid">
              {[...myPieces].sort((a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1)).map(piece => {
                const isSelected = selectedPiece?.id === piece.id;
                let displayShape = piece.shape;

                if (isSelected) {
                  for (let i = 0; i < pieceTransform.rotation; i++) {
                    displayShape = rotatePiece(displayShape);
                  }
                  if (pieceTransform.flipped) {
                    displayShape = flipPiece(displayShape);
                  }
                }

                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                displayShape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;

                return (
                  <div
                    key={piece.id}
                    className={`piece ${piece.used ? 'used' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handlePieceSelect(piece)}
                    draggable={!piece.used && isMyTurn && gameState === 'playing'}
                    onDragStart={(e) => handleDragStart(e, piece)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="piece-grid" style={{ width: `${cols * 11}px` }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const actualR = r + minRow;
                          const actualC = c + minCol;
                          const isFilled = displayShape[actualR]?.[actualC] === 1;
                          return (
                            <div
                              key={`${r}-${c}`}
                              className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                              style={{
                                backgroundColor: isFilled ? getColorStyles(playerColor).bg : 'transparent',
                                borderColor: isFilled ? getColorStyles(playerColor).border : 'rgba(0, 0, 0, 0.9)',
                                visibility: isFilled ? 'visible' : 'hidden'
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="board-container">
          <div className="board multiplayer-board" style={{
            '--player1-color': getColorStyles(playerColor).bg,
            '--player1-border': getColorStyles(playerColor).border,
            '--player2-color': getColorStyles(getContrastingColor(playerColor)).bg,
            '--player2-border': getColorStyles(getContrastingColor(playerColor)).border
          }}>
            {boardDisplay.map((row, r) => (
              <div key={r} className="board-row">
                {row.map((cell, c) => {
                  const isLegal = !pendingMove && legalPositions.some(pos => pos.row === r && pos.col === c);
                  const myCorner = myPlayerInfo?.startingCorner || [4, 4];
                  const oppCorner = myCorner[0] === 4 ? [9, 9] : [4, 4]; // Opponent has the opposite corner
                  const isMyStart = r === myCorner[0] && c === myCorner[1];
                  const isOppStart = r === oppCorner[0] && c === oppCorner[1];
                  const isPlaced = placedCells.has(`${r},${c}`);

                  const isMyPiece = cell === myPlayerInfo?.playerId;
                  const isOpponentPiece = cell && cell !== myPlayerInfo?.playerId && cell !== 'preview';
                  const player1Styles = isMyPiece ? getColorStyles(playerColor) : {};
                  const player2Styles = isOpponentPiece ? getColorStyles(getContrastingColor(playerColor)) : {};
                  const cellStyle = isMyPiece ? {
                    backgroundColor: player1Styles.bg,
                    borderColor: player1Styles.border
                  } : isOpponentPiece ? {
                    backgroundColor: player2Styles.bg,
                    borderColor: player2Styles.border
                  } : {};

                  return (
                    <div
                      key={c}
                      className={`cell ${isMyPiece ? 'player1' : isOpponentPiece ? 'player2' : ''}
                                 ${cell === 'preview' ? 'preview' : ''}
                                 ${isLegal ? 'legal' : ''}
                                 ${isMyStart ? 'start-corner-p1' : ''}
                                 ${isOppStart ? 'start-corner-p2' : ''}
                                 ${isPlaced ? 'placed' : ''}`}
                      style={cellStyle}
                      onClick={(e) => handleBoardClick(e, r, c)}
                      onDragOver={(e) => handleDragOver(e, r, c)}
                      onDrop={(e) => handleDrop(e, r, c)}
                      onMouseEnter={() => handleMouseEnter(r, c)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          <div className="controls">
            <button
              type="button"
              onClick={handleRotate}
              disabled={!isMyTurn || !selectedPiece || gameState !== 'playing'}
            >
              Rotate
            </button>
            <button
              type="button"
              onClick={handleFlip}
              disabled={!isMyTurn || !selectedPiece || gameState !== 'playing'}
            >
              Flip
            </button>
            <button
              type="button"
              onClick={handleUndo}
              className="undo-btn"
              disabled={!pendingMove || gameState !== 'playing'}
            >
              Undo
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="confirm-btn"
              disabled={!pendingMove || gameState !== 'playing' || connectionHealth === 'lost'}
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={handlePass}
              className="pass-btn"
              disabled={!isMyTurn || gameState !== 'playing' || connectionHealth === 'lost'}
            >
              Pass
            </button>
          </div>
        </div>

        <div className="side-panel">
          <div className={`player-info ${!isMyTurn ? 'active' : ''}`}>
            <h3>{opponentInfo?.playerName}</h3>
            <div className="score-section">
              <div className="score-label">Score</div>
              <div className="score-value">{score.opponent}</div>
            </div>
            {!isMyTurn ? (
              <>
                <div className="turn-indicator">Their Turn</div>
                <div className="timer-section">
                  <div className="timer-text">{turnTimer}s</div>
                </div>
              </>
            ) : (
              <div className="turn-indicator">Your Turn!</div>
            )}
          </div>

          <div className="pieces-container opponent-pieces">
            <h3>Opponent Pieces</h3>
            <div className="pieces-grid">
              {[...opponentPieces].sort((a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1)).map(piece => {
                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                piece.shape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;

                return (
                  <div
                    key={piece.id}
                    className={`piece ${piece.used ? 'used' : ''}`}
                  >
                    <div className="piece-grid" style={{ width: `${cols * 11}px` }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const actualR = r + minRow;
                          const actualC = c + minCol;
                          const isFilled = piece.shape[actualR]?.[actualC] === 1;
                          const contrastColor = getContrastingColor(playerColor);
                          return (
                            <div
                              key={`${r}-${c}`}
                              className={`piece-cell ${isFilled ? 'filled player2-piece' : ''}`}
                              style={{
                                backgroundColor: isFilled ? getColorStyles(contrastColor).bg : 'transparent',
                                borderColor: isFilled ? getColorStyles(contrastColor).border : 'rgba(0, 0, 0, 0.9)',
                                visibility: isFilled ? 'visible' : 'hidden'
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiplayerGame;
